// ABOUTME: Component displaying today's behavior counts with Material-UI icons for each behavior type.
// ABOUTME: Shows all 6 behavior types (stars, triangles, squares, resets, 1:1 time, make it right) with counts and proper accessibility labels.

import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Star as StarIcon } from '@mui/icons-material';
import { StormIcon, TriangleIcon, PikoIcon, PonoIcon, KalaIcon } from '../utils/behaviorUtils';
import { decorativeGradients } from '../theme/tokens';

interface BehaviorCounts {
  stars: number;
  triangles: number;
  squares: number;
  flipYourCoins?: number;
  oneOnOneTimes?: number;
  makeItRights?: number;
}

interface TodaysBehaviorCountProps {
  behaviorCounts: BehaviorCounts;
  loading?: boolean;
  compact?: boolean;
}

const TodaysBehaviorCount: React.FC<TodaysBehaviorCountProps> = ({
  behaviorCounts,
  loading = false,
  compact = false,
}) => {
  const theme = useTheme();

  const behaviorItems = [
    // Row 1 (existing with subtitles)
    {
      type: 'squares',
      count: behaviorCounts.squares,
      icon: StormIcon,
      color: theme.palette.error.main,
      label: 'Storms (Meltdowns)',
      ariaLabel: `${behaviorCounts.squares} storms received today`
    },
    {
      type: 'triangles',
      count: behaviorCounts.triangles,
      icon: TriangleIcon,
      color: theme.palette.warning.main,
      label: 'Triangles (Minor Issues)',
      ariaLabel: `${behaviorCounts.triangles} triangles received today`
    },
    {
      type: 'stars',
      count: behaviorCounts.stars,
      icon: StarIcon,
      color: theme.palette.success.main,
      label: 'Stars (Good Behavior)',
      ariaLabel: `${behaviorCounts.stars} stars earned today`
    },
    // Row 2 (new - NO subtitles)
    {
      type: 'flipYourCoins',
      count: behaviorCounts.flipYourCoins || 0,
      icon: PikoIcon,
      color: theme.palette.primary.main,
      label: 'Reset',
      ariaLabel: `${behaviorCounts.flipYourCoins || 0} resets today`
    },
    {
      type: 'makeItRights',
      count: behaviorCounts.makeItRights || 0,
      icon: PonoIcon,
      color: theme.palette.secondary.main,
      label: 'Make It Right',
      ariaLabel: `${behaviorCounts.makeItRights || 0} make it rights today`
    },
    {
      type: 'oneOnOneTimes',
      count: behaviorCounts.oneOnOneTimes || 0,
      icon: KalaIcon,
      color: theme.palette.primary.main,
      label: 'Let Go',
      ariaLabel: `${behaviorCounts.oneOnOneTimes || 0} quality time sessions today`
    }
  ];

  if (loading) {
    if (compact) {
      return (
        <Box role="status" aria-label="Loading today's behavior counts">
          <Typography variant="body2" color="text.secondary">Loading...</Typography>
        </Box>
      );
    }
    return (
      <Paper
        elevation={2}
        sx={{ p: 3, borderRadius: 2, background: decorativeGradients.lightCard }}
        role="status"
        aria-label="Loading today's behavior counts"
      >
        <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
          Today&apos;s Behavior
        </Typography>
        <Typography variant="body2" color="text.secondary">Loading...</Typography>
      </Paper>
    );
  }

  const totalBehaviors =
    behaviorCounts.stars +
    behaviorCounts.triangles +
    behaviorCounts.squares +
    (behaviorCounts.flipYourCoins || 0) +
    (behaviorCounts.oneOnOneTimes || 0) +
    (behaviorCounts.makeItRights || 0);

  const iconFontSize = compact ? 20 : 32;
  const countVariant: 'h4' | 'h6' = compact ? 'h6' : 'h4';
  const cellPadding = compact ? 1 : 2;

  const content = (
    <>
      {!compact && (
        <Typography
          variant="h6"
          gutterBottom
          sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}
        >
          Today&apos;s Behavior
        </Typography>
      )}

      {totalBehaviors === 0 ? (
        <Box textAlign="center" py={compact ? 1 : 2}>
          <Typography variant={compact ? 'body2' : 'body1'} color="text.secondary">
            No behavior recorded yet today
          </Typography>
          {!compact && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Start tracking to see progress!
            </Typography>
          )}
        </Box>
      ) : (
        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={compact ? 1 : 2} sx={{ width: '100%' }}>
          {behaviorItems.map((item) => (
            <Box key={item.type}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                sx={{
                  p: cellPadding,
                  borderRadius: 1,
                  backgroundColor: compact ? 'transparent' : alpha(theme.palette.common.white, 0.7),
                  transition: 'all 0.2s ease',
                  ...(!compact && {
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.common.white, 0.9),
                      transform: 'translateY(-2px)',
                    },
                  }),
                }}
                role="group"
                aria-label={item.ariaLabel}
              >
                <item.icon
                  sx={{
                    fontSize: iconFontSize,
                    color: item.color,
                    mb: compact ? 0.25 : 1,
                    filter: compact ? undefined : `drop-shadow(0 2px 4px ${alpha(theme.palette.common.black, 0.1)})`,
                  }}
                  aria-hidden="true"
                />
                <Typography
                  variant={countVariant}
                  sx={{
                    // Exception: variant is chosen dynamically (h4/h6), so no single baseline weight applies
                    fontWeight: 'bold',
                    color: item.color,
                    lineHeight: 1,
                    mb: compact ? 0 : 0.5,
                  }}
                  aria-label={`Count: ${item.count}`}
                >
                  {item.count}
                </Typography>
                {!compact && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      textAlign: 'center',
                      fontSize: '0.7rem',
                      lineHeight: 1.2,
                    }}
                  >
                    {item.label}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {!compact && totalBehaviors > 0 && (
        <Box mt={2} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Total behaviors today: {totalBehaviors}
          </Typography>
        </Box>
      )}
    </>
  );

  if (compact) {
    return (
      <Box role="region" aria-label="Today's behavior summary" sx={{ width: '100%' }}>
        {content}
      </Box>
    );
  }

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        borderRadius: 2,
        background: decorativeGradients.lightCard,
      }}
      role="region"
      aria-label="Today's behavior summary"
    >
      {content}
    </Paper>
  );
};

export default TodaysBehaviorCount;